import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import SuperJSON from "superjson";
import { match } from "ts-pattern";

import { type DocumentMetadata } from "@moment/api-collab/api-types";

import { type PageProps } from "~/pages/d/[workspaceName]/[documentId]/[...pageSlug].page";
import { useDocumentActor, usePageActor } from "~/state";

import { Easel } from "../canvas/easel/Easel";
import { createEditorState } from "../canvas/easel/editor-state/createEditorState";
import { BigRichTextEditor } from "../canvas/editor/BigRichTextEditor";
import { CanvasFailure } from "../canvas/views/CanvasFailure";
import { PublishedDocument } from "../canvas/views/PublishedDocument";
import { TemplateVitePressDocs } from "../canvas/views/TemplateVitePressDocs";
import { DocumentTopBar } from "../canvases/DocumentTopBar";

export const SsrPage: React.FC<PageProps> = ({
	workspaceName,
	documentId,
	pageId,
	documentMetadata: documentMetadataString,
	node: nodeString,
}) => {
	const documentActor = useDocumentActor(documentId);
	const pageActor = usePageActor(documentId, pageId);

	useEffect(() => {
		documentActor?.send({ type: "document.opened" });
	}, [documentActor]);

	useEffect(() => {
		pageActor?.send({ type: "page.opened" });
	}, [pageActor]);

	if (!documentMetadataString || !nodeString) {
		return (
			<CanvasFailure
				error="Failed to fetch document metadata"
				resetErrorBoundary={() => {
					window.location.reload();
				}}
			/>
		);
	}

	const documentMetadata = SuperJSON.parse<DocumentMetadata>(documentMetadataString);
	const node = SuperJSON.parse<JsonObject>(nodeString);

	const editorState = createEditorState({ workspaceName, documentId, pageId, doc: node });

	return (
		<>
			<DocumentTopBar />

			<ErrorBoundary FallbackComponent={CanvasFailure}>
				<Easel
					workspaceName={workspaceName}
					documentId={documentId}
					pageId={pageId}
					documentInteractionMode="static"
				>
					<BigRichTextEditor document={documentMetadata} editorState={editorState}>
						{match(documentMetadata?.publishSettings?.template)
							.with("blog", () => <PublishedDocument canvas={documentMetadata} />)
							.otherwise(() => (
								<TemplateVitePressDocs canvas={documentMetadata} />
							))}
					</BigRichTextEditor>
				</Easel>
			</ErrorBoundary>
		</>
	);
};
